import { useEffect, useState } from 'react'
import { supabase } from '@fpv-boats/supabase'
import type {
  RaceParticipant,
  Race,
  Lap,
} from '../types'

export function useRealtimeRace(raceId: string) {
  const [race, setRace] = useState<Race | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!raceId) return

    // Initial fetch
    const fetchRace = async () => {
      try {
        const { data, error: err } = await supabase
          .from('races')
          .select('*')
          .eq('id', raceId)
          .single()

        if (err) throw err
        setRace(data)
        setLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch race'))
        setLoading(false)
      }
    }

    fetchRace()

    // Subscribe to changes
    const channel = supabase
      .channel(`race-${raceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'races',
          filter: `id=eq.${raceId}`,
        },
        (payload) => {
          setRace(payload.new as Race)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [raceId])

  return { race, loading, error }
}

export function useRealtimeParticipants(raceId: string) {
  const [participants, setParticipants] = useState<RaceParticipant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!raceId) return

    // Initial fetch
    const fetchParticipants = async () => {
      try {
        const { data, error: err } = await supabase
          .from('race_participants')
          .select('*')
          .eq('race_id', raceId)
          .order('current_position', { ascending: true })

        if (err) throw err
        setParticipants(data || [])
        setLoading(false)
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to fetch participants')
        )
        setLoading(false)
      }
    }

    fetchParticipants()

    // Subscribe to changes
    const channel = supabase
      .channel(`participants-${raceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'race_participants',
          filter: `race_id=eq.${raceId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setParticipants((prev) => prev.filter((p) => p.id !== (payload.old as RaceParticipant).id))
          } else {
            setParticipants((prev) => {
              const newParticipant = payload.new as RaceParticipant
              const index = prev.findIndex((p) => p.id === newParticipant.id)
              if (index >= 0) {
                const updated = [...prev]
                updated[index] = newParticipant
                return updated.sort(
                  (a, b) => a.current_position - b.current_position
                )
              }
              return [...prev, newParticipant].sort(
                (a, b) => a.current_position - b.current_position
              )
            })
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [raceId])

  return { participants, loading, error }
}

export function useRealtimeLaps(raceId: string) {
  const [laps, setLaps] = useState<Lap[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!raceId) return

    // Initial fetch
    const fetchLaps = async () => {
      try {
        const { data, error: err } = await supabase
          .from('laps')
          .select('*')
          .eq('race_id', raceId)
          .order('created_at', { ascending: false })

        if (err) throw err
        setLaps(data || [])
        setLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch laps'))
        setLoading(false)
      }
    }

    fetchLaps()

    // Subscribe to new laps
    const channel = supabase
      .channel(`laps-${raceId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'laps',
          filter: `race_id=eq.${raceId}`,
        },
        (payload) => {
          setLaps((prev) => [payload.new as Lap, ...prev])
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [raceId])

  return { laps, loading, error }
}

export function useRealtimeParticipant(participantId: string) {
  const [participant, setParticipant] = useState<RaceParticipant | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!participantId) return

    // Initial fetch
    const fetchParticipant = async () => {
      try {
        const { data, error: err } = await supabase
          .from('race_participants')
          .select('*')
          .eq('id', participantId)
          .single()

        if (err) throw err
        setParticipant(data)
        setLoading(false)
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to fetch participant')
        )
        setLoading(false)
      }
    }

    fetchParticipant()

    // Subscribe to changes
    const channel = supabase
      .channel(`participant-${participantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'race_participants',
          filter: `id=eq.${participantId}`,
        },
        (payload) => {
          setParticipant(payload.new as RaceParticipant)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [participantId])

  return { participant, loading, error }
}
